import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Upload, Loader2, Search, Database, Building2, FileText, Calendar,
  CheckCircle2, AlertTriangle, X, Trash2, RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}-${m}-${y}`;
  } catch { return iso; }
};

const titleCase = (s) => {
  if (!s) return '';
  return String(s)
    .toLocaleLowerCase('es')
    .split(/(\s+)/)
    .map((w) => (w.length && !/\s/.test(w) ? w.charAt(0).toLocaleUpperCase('es') + w.slice(1) : w))
    .join('');
};

const formatRut = (rut) => {
  if (!rut) return '—';
  // Deja como está si ya viene con puntos/guión
  const raw = String(rut).replace(/[.\-\s]/g, '').toUpperCase();
  if (raw.length < 2) return rut;
  const dv = raw.slice(-1);
  const num = raw.slice(0, -1);
  const withDots = num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withDots}-${dv}`;
};

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('…');
    out.push(sorted[i]);
  }
  return out;
};

export default function CapacitadorAdminInputs() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const fileRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/capacitador/admin/contratos`, {
        headers, params: { q: q || undefined, limit: 500 },
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de contratos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, token]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  useEffect(() => { setPage(1); }, [q, rows.length]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      toast.error('El archivo debe ser CSV');
      return;
    }
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/capacitador/admin/contratos/import`,
        fd,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setLastResult(res.data);
      toast.success(
        `Importado: ${res.data.inserted} nuevos · ${res.data.updated} actualizados · ${res.data.errores} errores`
      );
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al importar CSV');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleWipe = async () => {
    setWiping(true);
    try {
      await axios.delete(`${API}/capacitador/admin/contratos`, { headers });
      toast.success('Tabla vaciada correctamente');
      setConfirmWipe(false);
      setLastResult(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo vaciar la tabla');
    } finally {
      setWiping(false);
    }
  };

  const total = rows.length;
  const uniqueEmpresas = new Set(rows.map(r => r.id_empresa_ext)).size;
  const vigentes = rows.filter(r => (r.estado || '').toLowerCase().includes('vigente')).length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const rangeFrom = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeTo = Math.min(currentPage * pageSize, total);

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="capacitador-admin-inputs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-teal-600" />
            <h1 className="text-2xl font-semibold text-slate-900">Admin Inputs · Contratos & Empresas</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Fuente maestra de <strong>Empresas</strong>, <strong>Gerencias</strong> y <strong>Contratos</strong> extraídos del ERP LEGAV. Usada por Reporte de Incidentes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1.5 px-3 h-9 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
          <button
            disabled={total === 0}
            onClick={() => setConfirmWipe(true)}
            className="inline-flex items-center gap-1.5 px-3 h-9 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="btn-wipe"
          >
            <Trash2 className="w-4 h-4" /> Vaciar tabla
          </button>
        </div>
      </div>

      {/* Uploader */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-start gap-4 flex-col md:flex-row md:items-center">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">Cargar archivo CSV</p>
            <p className="text-xs text-slate-500 mt-1">
              Los datos obtenidos son de la <strong>Carpeta de Arranque Tysta</strong>.
            </p>
          </div>
          <label
            htmlFor="csv-input"
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium text-white cursor-pointer transition-colors ${
              uploading ? 'bg-slate-400 cursor-wait' : 'bg-teal-600 hover:bg-teal-700'
            }`}
            data-testid="btn-upload-csv"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Procesando...' : 'Seleccionar CSV'}
          </label>
          <input
            ref={fileRef}
            id="csv-input"
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            data-testid="input-csv-file"
          />
        </div>
        {lastResult && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatChip label="Nuevos" value={lastResult.inserted} color="emerald" />
            <StatChip label="Actualizados" value={lastResult.updated} color="teal" />
            <StatChip label="Errores" value={lastResult.errores} color={lastResult.errores > 0 ? 'red' : 'slate'} />
            <StatChip label="Total en BD" value={lastResult.total_en_bd} color="slate" />
            <StatChip
              label="Estado"
              value={lastResult.errores === 0 ? 'OK' : 'Parcial'}
              color={lastResult.errores === 0 ? 'emerald' : 'amber'}
            />
            {lastResult.errores_muestra?.length > 0 && (
              <div className="col-span-full bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Muestra de errores (primeros 5):</p>
                <ul className="mt-1 space-y-0.5 list-disc list-inside">
                  {lastResult.errores_muestra.map((er, i) => (
                    <li key={i}>Línea {er.linea}: {er.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={FileText} label="Contratos totales" value={total} color="teal" />
        <MetricCard icon={Building2} label="Empresas" value={uniqueEmpresas} color="emerald" />
        <MetricCard icon={CheckCircle2} label="Vigentes" value={vigentes} color="sky" />
        <MetricCard icon={Calendar} label="Últ. import" value={rows[0]?.updated_at ? new Date(rows[0].updated_at).toLocaleDateString('es-CL') : '—'} color="slate" />
      </div>

      {/* Search + Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por RUT, razón social, N° acuerdo o gerencia..."
              className="w-full pl-9 pr-8 h-9 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400"
              data-testid="input-search"
            />
            {q && (
              <button
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="btn-clear-search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <span className="text-xs text-slate-500 ml-auto">
            {total === 0 ? '0 registros' : `Mostrando ${rangeFrom}–${rangeTo} de ${total}`}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...
          </div>
        ) : total === 0 ? (
          <div className="py-16 text-center text-slate-500 text-sm">
            <Database className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p>No hay datos cargados. Sube el CSV LEGAV para comenzar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="table-contratos">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">RUT</th>
                  <th className="px-3 py-2 text-left font-semibold">Empresa</th>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">N° Acuerdo</th>
                  <th className="px-3 py-2 text-left font-semibold">Descripción</th>
                  <th className="px-3 py-2 text-left font-semibold">Gerencia</th>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Inicio</th>
                  <th className="px-3 py-2 text-left font-semibold whitespace-nowrap">Término</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs text-slate-600 whitespace-nowrap">{formatRut(r.rut_empresa)}</td>
                    <td className="px-3 py-2 text-slate-800">{titleCase(r.razon_social) || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{r.numero_acuerdo}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-md truncate" title={r.descripcion || ''}>{titleCase(r.descripcion) || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{titleCase(r.nombre_gerencia) || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.fecha_inicio)}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(r.fecha_termino)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50" data-testid="pagination">
                <span className="text-xs text-slate-500">
                  Página {currentPage} de {totalPages}
                </span>
                <div className="inline-flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={currentPage === 1}
                    className="px-2 h-8 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="btn-page-first"
                  >« Primera</button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 h-8 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="btn-page-prev"
                  >‹ Anterior</button>
                  {getPageNumbers(currentPage, totalPages).map((n, i) =>
                    n === '…' ? (
                      <span key={`e${i}`} className="px-2 text-xs text-slate-400">…</span>
                    ) : (
                      <button
                        key={n}
                        onClick={() => setPage(n)}
                        className={`min-w-[32px] px-2 h-8 text-xs rounded border ${
                          n === currentPage
                            ? 'bg-teal-600 border-teal-600 text-white font-semibold'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`btn-page-${n}`}
                      >{n}</button>
                    )
                  )}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 h-8 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="btn-page-next"
                  >Siguiente ›</button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 h-8 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="btn-page-last"
                  >Última »</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm wipe modal */}
      {confirmWipe && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="modal-confirm-wipe">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> Vaciar tabla de contratos
              </h3>
            </div>
            <div className="p-5 text-sm text-slate-600 space-y-2">
              <p>Esta acción eliminará los <strong>{total} registro(s)</strong> importados. El formulario de Incidentes no mostrará contratos hasta que se vuelva a subir el CSV.</p>
              <p className="text-xs text-slate-500">No afecta a las empresas colaboradoras, sólo al catálogo de contratos.</p>
            </div>
            <div className="p-4 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={() => setConfirmWipe(false)}
                className="px-4 h-9 text-sm border border-slate-200 rounded-lg hover:bg-slate-50"
                data-testid="btn-cancel-wipe"
              >Cancelar</button>
              <button
                onClick={handleWipe}
                disabled={wiping}
                className="px-4 h-9 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="btn-confirm-wipe"
              >
                {wiping && <Loader2 className="w-4 h-4 animate-spin" />}
                Vaciar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, color = 'slate' }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`border rounded-lg px-3 py-2 ${colors[color] || colors.slate}`}>
      <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color = 'teal' }) {
  const colors = {
    teal: 'text-teal-600 bg-teal-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    sky: 'text-sky-600 bg-sky-50',
    slate: 'text-slate-600 bg-slate-100',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color] || colors.teal}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}
